/* Image URL utility
   Simplified to only use the product-images bucket in current Supabase project
   Updated: 2026-01-22 - Removed old bucket support
   Updated: 2026-09-24 - Added Supabase image transformation params (width, quality, format) for optimized delivery
   Updated: 2026-09-24 - Route images through /img/ proxy for long-lived browser caching
   Updated: 2026-09-24 - Fixed: use SDK transform option so Supabase serves from render endpoint */

import { supabase } from './supabase';

export type ImageSize = 'thumb' | 'small' | 'medium' | 'large' | 'original';

const SIZE_WIDTHS: Record<Exclude<ImageSize, 'original'>, number> = {
  thumb: 200,
  small: 400,
  medium: 600,
  large: 800,
};

const SUPABASE_HOST = 'iiwewbpbiqrjdkptmrpd.supabase.co';
const RENDER_PREFIX = `https://${SUPABASE_HOST}/storage/v1/render/image/public/`;
const OBJECT_PREFIX = `https://${SUPABASE_HOST}/storage/v1/object/public/`;

/**
 * Rewrite a Supabase URL to go through our proxy paths.
 * - Transformed images (render endpoint) -> /img/ (1-year cache, immutable)
 * - Original images (object endpoint) -> /img-raw/ (1-year cache, immutable)
 * Both proxy paths are configured in _redirects (production) and vite.config.ts (dev).
 */
function proxySupabaseUrl(url: string): string {
  if (url.startsWith(RENDER_PREFIX)) {
    return `/img/${url.slice(RENDER_PREFIX.length)}`;
  }
  if (url.startsWith(OBJECT_PREFIX)) {
    return `/img-raw/${url.slice(OBJECT_PREFIX.length)}`;
  }
  return url;
}

/**
 * Get product image URL from the product-images bucket
 * @param storagePath - The storage path from the database (can be full URL or path)
 * @param _usesNewBucket - Deprecated, kept for backwards compatibility. Ignored.
 * @param size - Desired output size preset (defaults to 'small' / 400px)
 * @returns The public URL for the image, optimized and proxied through our domain
 */
export function getProductImageUrl(
  storagePath: string,
  _usesNewBucket?: boolean,
  size: ImageSize = 'small',
): string {
  if (!storagePath) return '';

  // If it's already a full URL, proxy it if it's from our Supabase project
  if (storagePath.startsWith('http')) {
    if (size === 'original') return storagePath;
    if (storagePath.includes(SUPABASE_HOST)) {
      return proxySupabaseUrl(appendTransformParams(storagePath, size));
    }
    return storagePath;
  }

  // Build URL from the product-images bucket using SDK transform support
  if (size === 'original') {
    const { data } = supabase.storage.from('product-images').getPublicUrl(storagePath);
    return proxySupabaseUrl(data.publicUrl);
  }

  const { data } = supabase.storage.from('product-images').getPublicUrl(storagePath, {
    transform: { width: SIZE_WIDTHS[size], resize: 'contain', quality: 80, format: 'webp' },
  });
  return proxySupabaseUrl(data.publicUrl);
}

/**
 * Get blog image URL
 * @param storagePath - The storage path from the database
 * @param size - Desired output size preset (defaults to 'medium' / 600px)
 * @returns The public URL for the blog image, optimized and proxied through our domain
 */
export function getBlogImageUrl(storagePath: string, size: ImageSize = 'medium'): string {
  if (!storagePath) return '';

  // If it's already a full URL, proxy it if it's from our Supabase project
  if (storagePath.startsWith('http')) {
    if (size === 'original') return storagePath;
    if (storagePath.includes(SUPABASE_HOST)) {
      return proxySupabaseUrl(appendTransformParams(storagePath, size));
    }
    return storagePath;
  }

  // Build URL from the blog-images bucket using SDK transform support
  if (size === 'original') {
    const { data } = supabase.storage.from('blog-images').getPublicUrl(storagePath);
    return proxySupabaseUrl(data.publicUrl);
  }

  const { data } = supabase.storage.from('blog-images').getPublicUrl(storagePath, {
    transform: { width: SIZE_WIDTHS[size], resize: 'contain', quality: 80, format: 'webp' },
  });
  return proxySupabaseUrl(data.publicUrl);
}

/**
 * For full URLs that already point to Supabase object endpoint, switch to render endpoint
 * and append transform query params.
 */
function appendTransformParams(url: string, size: ImageSize): string {
  const width = SIZE_WIDTHS[size];
  // Convert object endpoint to render endpoint for transformations
  const renderUrl = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  const separator = renderUrl.includes('?') ? '&' : '?';
  return `${renderUrl}${separator}width=${width}&resize=contain&quality=80&format=webp`;
}

/**
 * @deprecated Use getProductImageUrl or getBlogImageUrl instead
 */
export function transformImageUrl(url: string): string {
  return url;
}

/**
 * @deprecated Use getProductImageUrl or getBlogImageUrl instead
 */
export function getTransformedPublicUrl(storagePath: string): string {
  return getProductImageUrl(storagePath);
}
